'use client';

import useSWR from 'swr';
import { useAuth } from '../lib/auth';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';

type Owner = {
  id: number;
  code: string;
  name: string;
  ico: string | null;
  isActive: boolean;
};

export type OwnerOption = Owner;

export function useOwners(enabled = true) {
  const { isAuthenticated, mounted } = useAuth();
  const { data, error, isLoading } = useSWR<Owner[]>(
    enabled && mounted && isAuthenticated ? '/api/user/armorik/accounting/owners?activeOnly=true' : null,
  );
  return { owners: data, error, isLoading };
}

export function OwnerSelector({
  value,
  onChange,
  allowGlobal = true,
  className = '',
  label,
}: {
  value: number | null;
  onChange: (ownerId: number | null) => void;
  allowGlobal?: boolean;
  className?: string;
  label?: string;
}) {
  const { t } = useTranslation();
  const { owners, isLoading } = useOwners();

  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-bark mb-1">{label || t('compOwnerSelectorLabel')}</label>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
        disabled={isLoading}
        className="p-2 border border-wheat rounded bg-linen/50 text-bark text-sm focus:ring-1 focus:ring-moss outline-none min-w-[200px]"
      >
        {allowGlobal && <option value="">{t('compOwnerSelectorGlobal')}</option>}
        {!allowGlobal && <option value="">{t('compOwnerSelectorSelectOwner')}</option>}
        {owners?.map(o => (
          <option key={o.id} value={o.id}>
            {o.name}{o.ico ? ` · ${o.ico}` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Read-only zobrazení vybrané jednotky — pro stránky, kde jednotka přichází
 *  z nadřazeného kontextu (mzdy: vybrána na dashboardu, práva budou per-jednotka). */
export function OwnerBadge({ ownerId, className = '', label }: { ownerId: number | null; className?: string; label?: string }) {
  const { t } = useTranslation();
  const { owners } = useOwners();
  const owner = owners?.find(o => o.id === ownerId);
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-bark mb-1">{label || t('compOwnerSelectorLabel')}</label>
      <span className="inline-block p-2 border border-wheat/60 rounded bg-wheat/20 text-bark text-sm min-w-[200px]">
        {owner ? `${owner.name}${owner.ico ? ` · ${owner.ico}` : ''}` : '…'}
      </span>
    </div>
  );
}
